/**
 * Module dependencies.
 */

import { FormList } from 'app/components/organisms/crud/form-types';
import { requiredRule } from 'app/core/utils/field-rules';
import { TFunction } from 'react-i18next';
import { useCrudRequest } from 'app/hooks/app-requests/crud/use-crud-request';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import React from 'react';
import { DrugIcon, DrugIcons } from 'app/components/atoms/drug-icon/drug-icon';

/**
 * Serialize icon to ensure consistent JSON format.
 */

export const serializeIcon = (name: string, number: number) => {
  return JSON.stringify({ name, number });
};

/**
 * `iconOptions` constant
 */

const iconOptions = DrugIcons.map(icon => ({
  value: serializeIcon(icon.name, icon.number),
  label: (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <DrugIcon iconNumber={icon.number} size={20} />
      <span>
        {icon.name}
        {' ('}
        {icon.number}
        {')'}
      </span>
    </div>
  ),
  searchValue: `${icon.name} ${icon.number}`
}));

/**
 * Left.
 */

const left = (translate: TFunction, method: 'add' | 'edit'): FormList => [
  {
    type: 'inputField',
    name: 'registry',
    disabled: method === 'edit',
    label: translate('common.labels.registry'),
    rules: requiredRule(translate)
  },
  {
    type: 'inputField',
    name: 'name',
    translatable: true,
    label: translate('common.labels.name'),
    rules: requiredRule(translate)
  },
  {
    type: 'inputField',
    name: 'productName',
    translatable: true,
    label: translate('common.labels.productName'),
    rules: requiredRule(translate)
  },
  {
    type: 'inputField',
    name: 'productDescription',
    translatable: true,
    label: translate('common.labels.productDescription'),
    rules: requiredRule(translate)
  },
  {
    type: 'inputField',
    name: 'dci',
    label: translate('common.labels.dci'),
    rules: undefined
  },
  {
    type: 'inputField',
    inputType: 'textArea',
    translatable: true,
    name: 'therapyInformation',
    label: translate('common.labels.therapyInformation'),
    rules: undefined
  },
  {
    type: 'inputField',
    inputType: 'textArea',
    translatable: true,
    name: 'recommendations',
    label: translate('common.labels.recommendations'),
    rules: undefined
  },
  {
    type: 'timePickerField',
    name: 'predeterminedTime',
    label: translate('common.labels.predeterminedTime'),
    rules: undefined
  },
  {
    type: 'columns',
    name: 'leftWire1',
    columns: [
      {
        column: 8,
        children: [
          {
            type: 'inputNumberField',
            name: 'pillsNumber',
            formatter: value => value,
            label: translate('common.labels.pillsNumber'),
            rules: requiredRule(translate)
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'inputNumberField',
            name: 'dosage',
            formatter: value => value,
            label: translate('common.labels.dosage'),
            rules: requiredRule(translate)
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'inputNumberField',
            name: 'maxRecommendedDosage',
            formatter: value => value,
            label: translate('common.labels.maxRecommendedDosage'),
            rules: undefined
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'inputNumberField',
            name: 'packagingSize',
            formatter: value => value,
            label: translate('common.labels.packagingSize'),
            rules: requiredRule(translate)
          }
        ]
      }
    ]
  },
  {
    type: 'columns',
    name: 'leftWrite2',
    columns: [
      {
        column: 8,
        children: [
          {
            type: 'inputNumberField',
            name: 'activeDays',
            formatter: value => value,
            label: translate('common.labels.activeDays'),
            rules: undefined
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'inputNumberField',
            name: 'pauseDays',
            formatter: value => value,
            label: translate('common.labels.pauseDays'),
            rules: undefined
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'inputNumberField',
            name: 'cnpem',
            formatter: value => value,
            label: translate('common.labels.cnpem'),
            rules: undefined
          }
        ]
      }
    ]
  },
  {
    type: 'columns',
    name: 'leftWrite3',
    columns: [
      {
        column: 8,
        children: [
          {
            type: 'inputNumberField',
            name: 'therapeuticClassificationCode',
            formatter: value => value,
            label: translate('common.labels.therapeuticClassificationCode'),
            rules: undefined
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'inputField',
            name: 'atcClass',
            label: translate('common.labels.atcClass'),
            rules: undefined
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'inputField',
            name: 'cci',
            label: translate('common.labels.cci'),
            placeholder: translate('common.labels.cciPlaceholder'),
            rules: undefined
          }
        ]
      }
    ]
  },
  {
    type: 'columns',
    name: 'leftWrite2',
    columns: [
      {
        column: 8,
        children: [
          {
            type: 'checkBoxField',
            name: 'chronic',
            label: translate('common.labels.chronic'),
            rules: undefined
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'checkBoxField',
            name: 'active',
            label: translate('common.labels.active'),
            rules: undefined
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'checkBoxField',
            name: 'requiresPrescription',
            label: translate('common.labels.requiresPrescription'),
            rules: undefined
          }
        ]
      }
    ]
  },
  {
    type: 'selectField',
    name: 'icon',
    label: translate('common.labels.icon'),
    rules: requiredRule(translate),
    showSearch: true,
    options: iconOptions
  },
  {
    type: 'columns',
    name: 'leftWrite5',
    columns: [
      {
        column: 8,
        children: [
          {
            type: 'selectField',
            name: 'productUnits',
            showSearch: true,
            allowClear: true,
            label: translate('common.labels.productUnits'),
            rules: requiredRule(translate),
            options: {
              hook: useCrudRequest as any,
              hookProps: () => {
                return [
                  {
                    key: ['posology-enum-quantity'],
                    endpoint: 'posologyEnumQuantity' as any
                  }
                ];
              },
              normalize: (items: any[]) => {
                if (!items || !Array.isArray(items)) {
                  return [];
                }
                return items
                  .filter(item => item && item.code)
                  .map(item => ({
                    value: item.code,
                    label: item.description?.pt || item.code,
                    searchValue: item.description?.pt || item.code
                  }));
              }
            }
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'selectField',
            name: 'class',
            label: translate('common.labels.class'),
            rules: requiredRule(translate),
            options: [
              { value: 'medicine', label: 'Medicine' },
              { value: 'supplement', label: 'Supplement' }
            ]
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'selectField',
            name: 'country',
            showSearch: true,
            allowClear: true,
            label: translate('common.labels.country'),
            rules: requiredRule(translate),
            options: {
              hook: useCrudRequest as any,
              hookProps: () => {
                return [
                  {
                    key: ['countries'],
                    endpoint: 'countries' as any
                  }
                ];
              },
              normalize: (items: any[]) => {
                if (!items || !Array.isArray(items)) {
                  return [];
                }
                return items
                  .filter(item => item && (item.code || item.id))
                  .map(item => {
                    // Parse JSON localized name and get Portuguese text
                    let name = item.code;
                    try {
                      const parsedName = typeof item.name === 'string' ? JSON.parse(item.name) : item.name;
                      name = parsedName?.pt || parsedName?.en || item.code;
                    } catch (e) {
                      // If parsing fails, use the name as is or fallback to code
                      name = item.name?.pt || item.name || item.code;
                    }

                    const flagUrl = resolveAppUrl('flags', { interpolations: { code: item.code } });

                    return {
                      value: item.code || item.id,
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <img alt={name} src={flagUrl} style={{ width: '20px', height: '15px' }} />
                          <span>{name}</span>
                        </div>
                      ),
                      searchValue: name
                    };
                  });
              }
            }
          }
        ]
      }
    ]
  }
];

/**
 * Right.
 */

const right = (translate: TFunction): FormList => [
  {
    type: 'inputField',
    name: 'id',
    disabled: true,
    label: translate('common.labels.id'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'createdAt',
    disabled: true,
    label: translate('common.labels.createdAt'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'updatedAt',
    disabled: true,
    label: translate('common.labels.updatedAt'),
    rules: undefined
  }
];

/**
 * Export `formFields`.
 */

export const formFields = (translate: TFunction, method: 'add' | 'edit'): FormList => [
  {
    type: 'columns',
    gutter: 40,
    name: 'mainWire',
    columns: [
      {
        column: 18,
        children: left(translate, method)
      },
      {
        column: 6,
        children: right(translate)
      }
    ]
  }
];
